<template>
  <v-container
    v-if="company"
    class="layout-container"
  >
    <BaseHeader
      :title="$t('avv.title')"
      :style-variant="10"
    >
      <template v-slot:design>
        <HeaderDesign />
      </template>

      <template v-slot:desc>
        <p>
          {{ $t('avv.subtitle') }}
        </p>
      </template>
      <template v-slot:content>
        <v-breadcrumbs :items="breadcrumbItems" />
      </template>
    </BaseHeader>

    <v-card
      v-if="!hasSeperateAvv"
      outlined
      class="pa-8"
    >
      <p>
        {{ $t('avv.separate-avv-texts.0') }}
        <router-link
          class="text-decoration-none"
          to="/license-agreement"
        >
          <span class="text-decoration-underline">{{ $t('avv.separate-avv-texts.1') }}</span>
        </router-link>
        {{ $t('avv.separate-avv-texts.2') }}
      </p>
    </v-card>

    <div v-else>
      <v-card
        v-if="isContractOwner"
        outlined
        style="overflow:hidden"
      >
        <v-row>
          <v-col
            v-if="$vuetify.breakpoint.smAndUp"
            sm="3"
          >
            <div class="design-wrapper">
              <Leaf
                class="design-elements"
                width="1000"
                style="top:-1200px; right: 100px; opacity: 0.2"
                fill
              />

              <Leaf
                class="design-elements"
                width="40"
                style="top:10px; right: 150px; opacity: 0.8"
                stroke
                :stroke-width="1"
              />

              <Leaf
                class="design-elements"
                width="40"
                style="top:10px; right:140px; opacity: 0.3"
                fill
              />

              <SingleCircle
                class="design-elements"
                width="20px"
                style="top: 90px; right: 190px; opacity: 0.8"
                stroke
              />

              <SingleCircle
                class="design-elements"
                width="20px"
                style="top: 90px; right: 185px; opacity: 0.3"
                fill
              />
            </div>
          </v-col>
          <v-col
            cols="12"
            sm="9"
            class="pa-6"
          >
            <v-alert
              v-if="showUpdateWarning"
              type="warning"
            >
              {{ $t('alerts.avv.warning') }}
            </v-alert>

            <div class="d-flex align-center">
              <v-icon class="mr-2">
                mdi-file
              </v-icon>
              <a
                :href="avvUrl"
                target="_blank"
                class="text-decoration-none"
              >
                <h4 class="pb-0 mb-0 ">
                  {{ $t('avv.title') }}
                  <v-icon
                    color="primary"
                    size="14"
                  >
                    mdi-open-in-new
                  </v-icon>
                </h4>
              </a>
            </div>

            <div
              v-if="showDefaultText"
              class="pt-2"
            >
              <span>
                {{ $t('alerts.avv.info') }}
              </span>
            </div>

            <div class="pt-2">
              <v-checkbox
                v-model="isAccept"
                :disabled="!canAccept"
                :label="$t('avv.accept-terms')"
              />
            </div>

            <div
              v-if="canAccept"
              class="justify-end d-flex"
            >
              <v-btn
                class="primary"
                :disabled="!isAccept"
                @click="submit"
              >
                {{ $t('buttons.avv.cta') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card>
      <v-card
        v-else
        outlined
        class="pa-8"
      >
        <div v-if="!isAccept">
          <p
            v-html="$t('avv.not-accepted-text', { readablePlatformName })"
          />
          <p
            v-html="$t('avv.reminder-text')"
          />
        </div>
        <div v-else>
          <p
            v-html="$t('avv.accepted-text')"
          />
        </div>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import AVV from '@/queries/Avv.gql'
import UPDATE_AVV from '@/queries/UpdateAvv.gql'
import featureMixin from '@/mixins/feature'
import HeaderDesign from '@/modules/licenseAgreement/HeaderDesign.vue'
import BaseHeader from '@/components/BaseHeader.vue'
import Leaf from '@/components/design-elements/leaf.vue'
import SingleCircle from '@/components/design-elements/singleCircle.vue'
import brandingMixin from '@/mixins/branding'
import bus, { eventNames } from '@/lib/eventBus'

export default {
  components: { BaseHeader, Leaf, SingleCircle, HeaderDesign },
  mixins: [featureMixin, brandingMixin],
  data () {
    return {
      isAccept: false,
      canAccept: true
    }
  },
  computed: {
    isContractOwner () {
      return this.$auth.user.isContractOwner
    },
    avvUrl () {
      return this.getFeature(this.featureNames.AVV).config.avvUrl
    },
    showDefaultText () {
      return !this.company.avv?.acceptedByUserId && !this.company.avv?.acceptedAt
    },
    showUpdateWarning () {
      return this.company.avv?.acceptedAt && this.updatedAvvExists
    },
    updatedAvvExists () {
      if (this.company.avv?.acceptedByUserId && !this.company.avv?.acceptedAt) return false
      const lastModifiedAt = this.getFeature(this.featureNames.AVV).config?.lastModifiedAt
      if (!lastModifiedAt) return false
      return new Date(this.company.avv?.acceptedAt).getTime() < new Date(lastModifiedAt).getTime()
    },
    hasSeperateAvv () {
      const licenseAgreementFeature = this.getFeature(this.featureNames.LICENSE_AGREEMENT)
      return !(licenseAgreementFeature && licenseAgreementFeature.isActive && licenseAgreementFeature.config.hasAvv)
    },
    breadcrumbItems () {
      return [
        { text: this.$t('navigation.labels.imprint'), to: { path: '/legal' }, disabled: false, exact: true },
        { text: this.$t('avv.title-short') }
      ]
    }
  },
  methods: {
    async submit () {
      try {
        const { data: { updateAvv } } = await this.$apollo.mutate({
          mutation: UPDATE_AVV,
          variables: {
            companyId: this.$auth.user.companyId
          },
          update: (store, { data: { updateAvv } }) => {
            const data = store.readQuery({ query: AVV, variables: { id: this.$auth.user.companyId } })
            data.company.avv.acceptedAt = updateAvv
            store.writeQuery({ query: AVV, data, variables: { id: this.$auth.user.companyId } })
          }
        })

        bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'success', text: this.$t('alerts.avv.success') })

        this.updateAcceptUI(updateAvv)
        this.$router.push('/dashboard')
      } catch (err) {
        bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: this.$t('alerts.avv.error') })
      }
    },

    updateAcceptUI (avvAcceptedByUserId) {
      this.isAccept = !!(avvAcceptedByUserId && !this.updatedAvvExists)
      this.canAccept = !this.isAccept
    }
  },
  apollo: {
    company: {
      query: AVV,
      variables () {
        return {
          id: this.$auth.user.companyId
        }
      },
      result ({ data }) {
        if (!data) return
        this.updateAcceptUI(data.company.avv.acceptedByUserId)
      },
      skip () {
        return this.$auth.loading || !this.$auth.isAuthenticated
      }
    }
  }
}
</script>
<style scoped>
.design-wrapper {
  position: relative;
  width:300px;
  height: 1px;
}

.design-elements{
  position: absolute;
  z-index: 1;
  pointer-events: none;
}
</style>
